// @flow
import React from 'react'
import { Link, useStaticQuery, graphql } from 'gatsby'
import classNames from 'classnames'
import styles from './speakers.module.css'

export const invitedSpeakers = [
  'gwen-franck',
  'victoria-tsoukala',
  'adriaan-van-der-weel',
  'sami-syrjamaki',
  'thed-van-leeuwen',
  'andrei-rostovtsev',
  'vanessa-proudman',
]

type Props = {
  includeSpeakers: string[],
  excludeSpeakers: string[],
  includeTbd: boolean,
  hasBiography: boolean,
  hideOrphans: boolean,
  isCentered: boolean,
  className?: string,
}

function Speakers({
  includeSpeakers,
  excludeSpeakers,
  includeTbd,
  hasBiography,
  hideOrphans,
  isCentered,
  className,
  ...props
}: Props) {
  const { allSpeakers } = useStaticQuery(graphql`
    query Speakers {
      allSpeakers: allMdx(
        filter: { fields: { sourceName: { eq: "speakers" } } }
      ) {
        edges {
          node {
            fields {
              slug
              path
            }
            exports {
              meta {
                name
                affiliation
                tbd
              }
            }
            excerpt
          }
        }
      }
    }
  `)
  return (
    <ul
      {...props}
      className={classNames(
        styles.list,
        hasBiography ? styles.hasBiography : null,
        className,
        {
          [styles.isCentered]: isCentered,
          [styles.hideOrphans]: hideOrphans,
        },
      )}
    >
      {allSpeakers.edges
        .map((edge) => ({
          slug: edge.node.fields.slug,
          path: edge.node.fields.path,
          ...edge.node.exports.meta,
          biography: edge.node.excerpt,
        }))
        .filter((speaker) => (!includeTbd ? !speaker.tbd : true))
        .filter((speaker) => {
          const isIncluded = includeSpeakers.includes(speaker.slug)
          const isExcluded = excludeSpeakers.includes(speaker.slug)
          if (
            includeSpeakers != null &&
            isIncluded &&
            excludeSpeakers != null &&
            isExcluded
          ) {
            throw new Error(
              `Speaker '${speaker.slug}' is both included and excluded`,
            )
          }
          if (includeSpeakers == null && excludeSpeakers == null) {
            return true
          }
          return (
            (includeSpeakers != null && isIncluded) ||
            (excludeSpeakers != null && !isExcluded)
          )
        })
        .sort((speakerA, speakerB) => {
          const indexA = invitedSpeakers.indexOf(speakerA.slug)
          const indexB = invitedSpeakers.indexOf(speakerB.slug)
          if (indexA !== -1 && indexB === -1) {
            return -1
          }
          if (indexA === -1 && indexB !== -1) {
            return 1
          }
          if (indexA === -1 && indexB === -1) {
            return 0
          }
          return (
            invitedSpeakers.indexOf(speakerA.slug) -
            invitedSpeakers.indexOf(speakerB.slug)
          )
        })
        .map((speaker) => {
          let photo
          let info

          if (speaker.tbd) {
            photo = <span className={styles.photoContainer} />
            info = (
              <span className={styles.info}>
                <span className={styles.name} itemProp="name">
                  {speaker.name}
                </span>
              </span>
            )
          } else {
            const image = (
              <img
                className={styles.photo}
                alt={speaker.name}
                src={`https://res.cloudinary.com/pubmet/image/upload/c_fill,g_face,h_512,w_512/v3/speakers/${speaker.slug}.jpg`}
              />
            )
            photo = hasBiography ? (
              <Link className={styles.photoContainer} to={speaker.path}>
                {image}
              </Link>
            ) : (
              <span className={styles.photoContainer}>{image}</span>
            )
            info = (
              <span className={styles.info}>
                <span className={styles.name} itemProp="name">
                  {hasBiography ? (
                    <Link to={speaker.path}>{speaker.name}</Link>
                  ) : (
                    speaker.name
                  )}
                </span>
                <span className={styles.affiliation} itemProp="affiliation">
                  {speaker.affiliation}
                </span>
                {hasBiography ? (
                  <span className={styles.biography}>
                    <p>
                      {speaker.biography} <Link to={speaker.path}>More →</Link>
                    </p>
                  </span>
                ) : null}
              </span>
            )
          }

          return (
            <li
              key={speaker.slug}
              className={styles.item}
              itemScope
              itemType="http://schema.org/Person"
            >
              {hasBiography || speaker.isTbd ? (
                <>
                  {photo}
                  {info}
                </>
              ) : (
                <Link className={styles.link} to={speaker.path}>
                  {photo}
                  {info}
                </Link>
              )}
            </li>
          )
        })}
    </ul>
  )
}

Speakers.defaultProps = {
  includeSpeakers: [],
  excludeSpeakers: [],
  includeTbd: false,
  hasBiography: false,
  hideOrphans: false,
  isCentered: false,
}

export default Speakers
